import { useState } from 'react'

import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { useBlockToggle, useBlockedUsers, useMuteToggle, useMutedUsers } from '@/features/relations'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Link } from 'react-router-dom'

type Tab = 'blocks' | 'mutes'

/** 已屏蔽 / 已静音的名单，可以在这里解除。 */
export function SettingsRelationsPage() {
  const t = useT()
  const [tab, setTab] = useState<Tab>('blocks')
  const blocked = useBlockedUsers()
  const muted = useMutedUsers()
  const query = tab === 'blocks' ? blocked : muted

  const block = useBlockToggle()
  const mute = useMuteToggle()

  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  const users = query.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <>
      <PageHeader title={t('relations.title')} back>
        <nav className="flex" aria-label={t('relations.tabsLabel')}>
          {(['blocks', 'mutes'] as Tab[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}
              className={cn(
                'flex-1 py-4 text-[15px] transition-colors hover:bg-surface-hover',
                tab === key ? 'font-bold' : 'text-ink-muted',
              )}
            >
              <span
                className={cn('inline-block pb-3', tab === key && 'border-b-4 border-brand-ink')}
              >
                {key === 'blocks' ? t('relations.blockedTab') : t('relations.mutedTab')}
              </span>
            </button>
          ))}
        </nav>
      </PageHeader>

      <p className="border-b border-line px-4 py-3 text-[13px] text-ink-muted">
        {tab === 'blocks' ? t('relations.blockHint') : t('relations.muteHint')}
      </p>

      {query.isPending && <PostListSkeleton count={3} />}
      {!query.isPending && users.length === 0 && (
        <EmptyState title={tab === 'blocks' ? t('relations.noBlocked') : t('relations.noMuted')} />
      )}

      {users.map((user) => (
        <div key={user.id} className="flex items-center gap-3 border-b border-line px-4 py-3">
          <Avatar
            src={user.avatar_url}
            alt={user.display_name}
            size="sm"
            to={`/${user.username}`}
          />
          <Link to={`/${user.username}`} className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-bold hover:underline">{user.display_name}</p>
            <p className="truncate text-[15px] text-ink-muted">@{user.username}</p>
          </Link>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              tab === 'blocks'
                ? block.mutate({ username: user.username, blocking: true })
                : mute.mutate({ username: user.username, muting: true })
            }
          >
            {tab === 'blocks' ? t('relations.unblock') : t('relations.unmute')}
          </Button>
        </div>
      ))}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}
